/* eslint-disable camelcase */
import React from 'react'
import { AutoComplete, Col, Form, Radio, Row, InputNumber } from 'antd'
import PropTypes from 'prop-types'

import { LogoUploader } from '~components'
import { MultiSelectDropdown, Label, Checkbox, Input, RichTextInput } from '~stories'
import { getGradientCss, resolveFlagColorFromInput } from '~utils/flagColor'

import {
  FLAGS_COLOR_CODES,
  FLAG_APPS,
  FLAG_GRADIENT_DEFAULT_ANGLE,
  FLAG_GRADIENT_STOP_COUNTS,
  ROX_FLAG_SUB_TYPE
} from '../../constants'
import styles from './FlagBasicInfo.module.css'

const { Item } = Form

const COLOR_OPTIONS = FLAGS_COLOR_CODES.map(({ name, value }) => ({
  value,
  label: `${name} (${value})`
}))

const FlagBasicInfo = ({
  flag,
  handleAppNameChange,
  handleBackgroundGradientChange,
  handleColorChange,
  handleDescriptionChange,
  handleImageUrlUpdate,
  handlePriortyChange,
  handleShowIconChange,
  handleSubTypeChange,
  handleTitleChange
}) => {
  const {
    html_description,
    title,
    color,
    show_icon,
    icon_url: imageUrl,
    app_name = 'msg',
    flag_priority: flagPriority,
    sub_type: subType,
    background_gradient: backgroundGradient
  } = flag

  const isGradientMode = Boolean(backgroundGradient)
  const gradientStops = backgroundGradient?.stops ?? []
  const gradientStopCount = gradientStops.length || 2
  const gradientCss = getGradientCss(backgroundGradient)

  // Updates the gradient on the flag while preserving unchanged fields.
  // Pass `null` to clear the gradient entirely (switches back to solid mode).
  const updateGradient = patch => {
    if (!handleBackgroundGradientChange) return
    if (patch === null) {
      handleBackgroundGradientChange(null)
      return
    }
    const base = backgroundGradient ?? {
      type: 'linear',
      angle: FLAG_GRADIENT_DEFAULT_ANGLE,
      stops: [null, null]
    }
    handleBackgroundGradientChange({ ...base, ...patch })
  }

  const handleBackgroundModeChange = e => {
    const next = e.target.value
    if (next === 'gradient') {
      updateGradient({
        type: 'linear',
        angle: FLAG_GRADIENT_DEFAULT_ANGLE,
        stops: [null, null]
      })
    } else {
      updateGradient(null)
    }
  }

  const handleStopCountChange = e => {
    const nextCount = Number(e.target.value)
    const padded = [...gradientStops]
    while (padded.length < nextCount) padded.push(null)
    updateGradient({ stops: padded.slice(0, nextCount) })
  }

  const handleStopChange = (index, val) => {
    const nextStops = [...gradientStops]
    nextStops[index] = resolveFlagColorFromInput(val)
    updateGradient({ stops: nextStops })
  }

  return (
    <>
      <Input label={'Title'} onChange={handleTitleChange} value={title} />
      <RichTextInput label='Description' onChange={handleDescriptionChange} value={html_description}/>
      <Row>
        <Col span={8}>
          <Checkbox
            checked={show_icon}
            label="Show Icon"
            onChange={handleShowIconChange}
          />
        </Col>
        <Col span={8}>
          <Row>
            <Label
              isInline={false}
              value='Colors'
            /></Row>
          <Row>
            <Item style={{ marginBottom: 0 }}>
              <AutoComplete
                allowClear
                filterOption={(inputValue, option) => {
                  const v = (option.value || '').toLowerCase()
                  const label = (option.label || '').toLowerCase()
                  const q = (inputValue || '').toLowerCase()
                  return v.includes(q) || label.includes(q)
                }}
                onChange={val => handleColorChange(resolveFlagColorFromInput(val))}
                onSelect={val => handleColorChange(resolveFlagColorFromInput(val))}
                options={COLOR_OPTIONS}
                placeholder="Select a color or enter hex"
                style={{ width: 220 }}
                value={color?.value ?? ''}
              />
            </Item>
          </Row>
          {handleBackgroundGradientChange && app_name === 'msg' && (
            <div className={styles.gradientSection}>
              <Row className={styles.gradientRow}>
                <Label isInline={false} value='Background style' />
              </Row>
              <Row className={styles.gradientRow}>
                <Radio.Group onChange={handleBackgroundModeChange} value={isGradientMode ? 'gradient' : 'solid'}>
                  <Radio value='solid'>Solid color</Radio>
                  <Radio value='gradient'>Linear gradient</Radio>
                </Radio.Group>
              </Row>
              {isGradientMode && (
                <>
                  <Row className={styles.gradientRow}>
                    <Label isInline={false} value='Number of colors' />
                  </Row>
                  <Row className={styles.gradientRow}>
                    <Radio.Group onChange={handleStopCountChange} value={gradientStopCount}>
                      {FLAG_GRADIENT_STOP_COUNTS.map(n => (
                        <Radio key={n} value={n}>{n}</Radio>
                      ))}
                    </Radio.Group>
                  </Row>
                  {Array.from({ length: gradientStopCount }).map((_, idx) => (
                    <Row key={`gradient-stop-${idx}`} className={styles.gradientRow}>
                      <Label isInline={false} value={`Color ${idx + 1}`} />
                      <Item style={{ marginBottom: 0, width: '100%' }}>
                        <AutoComplete
                          allowClear
                          filterOption={(inputValue, option) => {
                            const v = (option.value || '').toLowerCase()
                            const label = (option.label || '').toLowerCase()
                            const q = (inputValue || '').toLowerCase()
                            return v.includes(q) || label.includes(q)
                          }}
                          onChange={val => handleStopChange(idx, val)}
                          onSelect={val => handleStopChange(idx, val)}
                          options={COLOR_OPTIONS}
                          placeholder='Select a color or enter hex'
                          style={{ width: '100%' }}
                          value={gradientStops[idx]?.value ?? ''}
                        />
                      </Item>
                    </Row>
                  ))}
                  <Row className={styles.gradientRow}>
                    <Label isInline={false} value='Preview' />
                  </Row>
                  <Row>
                    <div
                      className={`${styles.gradientPreview} ${gradientCss ? '' : styles.gradientPreviewEmpty}`}
                      style={gradientCss ? { background: gradientCss } : undefined}
                    >
                      {!gradientCss && 'Pick at least 2 colors to preview'}
                    </div>
                  </Row>
                </>
              )}
            </div>
          )}
        </Col>
        <Col span={8}>
          <Row>
            <Label
              isInline={false}
              value='App Name'
            /></Row>
          <Row>
            <MultiSelectDropdown
              allowClear={true}
              customStyle={{ width: 150 }}
              displayField="name"
              dropdownOptions={FLAG_APPS}
              handleChange={handleAppNameChange}
              selectedItems={[app_name]}
              valueField="value"
            />
          </Row>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={18}>
          <Label value='Image Url' />
          <div>
            <LogoUploader handleUpload={(thumbnailImageUrl, secureImageURL) => {
              const imageUrl = secureImageURL || thumbnailImageUrl
              handleImageUrlUpdate(imageUrl)
            }} logoUrl={imageUrl} />
          </div>
        </Col>
      </Row>
      {app_name === 'rox' && <Row gutter={16}>
        <Col span={8}>
          <Row>
            <Label
              isInline={false}
              value='Sequence'
            /></Row>
          <InputNumber defaultValue={1} min={1} onChange={handlePriortyChange} value={flagPriority} />
        </Col>
        <Col span={8}>
          <Row>
            <Label
              isInline={false}
              value='Sub Type'
            /></Row>
          <Row>
            <MultiSelectDropdown
              allowClear={true}
              customStyle={{ width: 150 }}
              displayField="name"
              dropdownOptions={ROX_FLAG_SUB_TYPE}
              handleChange={handleSubTypeChange}
              selectedItems={[subType]}
              valueField="value"
            />
          </Row>
        </Col>
      </Row>}
    </>
  )
}

FlagBasicInfo.propTypes = {
  flag: PropTypes.object.isRequired,
  handleAppNameChange: PropTypes.func,
  handleBackgroundGradientChange: PropTypes.func,
  handleColorChange: PropTypes.func,
  handleDescriptionChange: PropTypes.func.isRequired,
  handleImageUrlUpdate: PropTypes.func,
  handleLogoUpload: PropTypes.func,
  handlePriortyChange: PropTypes.func,
  handleShowIconChange: PropTypes.func,
  handleSubTypeChange: PropTypes.func,
  handleTitleChange: PropTypes.func.isRequired,
  handleTypeChange: PropTypes.func
}

FlagBasicInfo.defaultProps = {
  flag: {}
}

export default FlagBasicInfo
